// react
import { MouseEvent, useState } from 'react';

// types
import { Contract, ContractStatus, ErrorCause, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// utils
import { translateError } from '../../../utils/translations';

// props
export interface DialogUpdateContractStatusProps {
	contract: Contract;
	isOpen: boolean;
	status: ContractStatus;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateContractStatus = ({
	contract,
	isOpen,
	status,
	onClose,
	onSuccess,
}: DialogUpdateContractStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// state
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);

	const dialogByStatus = {
		SUBMITTED: {
			alert: t('dialogUpdateContractStatus.submitted.alert'),
			contentKey: 'dialogUpdateContractStatus.submitted.content',
			severity: 'warning' as const,
			submitLabel: t('submitForReview'),
			title: t('submitForReview'),
		},
		ACTIVE: {
			alert: t('dialogUpdateContractStatus.approved.alert'),
			contentKey: 'dialogUpdateContractStatus.approved.content',
			severity: 'success' as const,
			submitLabel: t('approveContract'),
			title: t('approveContract'),
		},
		DRAFT: {
			alert: t('dialogUpdateContractStatus.rejected.alert'),
			contentKey: 'dialogUpdateContractStatus.rejected.content',
			severity: 'error' as const,
			submitLabel: t('rejectContract'),
			title: t('rejectContract'),
		},
	};

	const dialog = dialogByStatus[status];

	const statusNext = status;

	const { fetchRequest: updateContractStatus, isLoading: isLoadingUpdateContractStatus } = useFetch(
		{
			isLazy: true,
			url: `${process.env.REACT_APP_API_URL}/contracts/${contract.id}`,
			options: {
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ status: statusNext }),
			},
			onError: (cause) => {
				const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
					items: [],
				};

				feedbackNew.items.push({
					alert: {
						children: translateError({ cause: cause as ErrorCause, t }),
						severity: 'error',
					},
				});
				setFeedback(feedbackNew);
			},
			onSuccess: () => {
				if (onSuccess) {
					onSuccess();
				}

				if (onClose) {
					onClose();
				}
			},
		}
	);

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		setFeedback(null);
		onClose(e);
	};

	const handleSubmit = () => {
		setFeedback(null);
		updateContractStatus();
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: dialog.submitLabel,
					color: status === 'DRAFT' ? 'error' : 'primary',
					loading: isLoadingUpdateContractStatus,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'contract',
			}}
			isOpen={isOpen}
			maxWidth={360}
			onClose={handleClose}
			subtitle={{
				children: contract.num,
			}}
			title={{
				children: dialog.title,
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.contentKey}
						values={{ num: contract.num }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity={dialog.severity}>{dialog.alert}</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
